import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Sparkles, Users, FileText, Video, MousePointer2, Twitter, Gift, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CLAIMS } from "@/config/claims";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ICONS = {
  signup: Users,
  content: FileText,
  auto_post: Twitter,
  video: Video,
  affiliate_click: MousePointer2,
  referral: Gift,
};

const KIND_TONE = {
  signup: "text-teal-400 bg-teal-500/10 border-teal-500/30",
  content: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
  auto_post: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  video: "text-purple-400 bg-purple-500/10 border-purple-500/30",
  affiliate_click: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  referral: "text-fuchsia-400 bg-fuchsia-500/10 border-fuchsia-500/30",
};

export default function WallOfWinsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/wins?limit=60`);
        if (mounted) setData(res.data);
      } catch (e) {
        // Soft fail — page still renders with totals=0 fallback
      }
      if (mounted) setLoading(false);
    };
    load();
    // refresh every 30s to keep the wall feeling alive
    const t = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const totals = data?.totals || { users: 0, content_pieces: 0, affiliate_clicks: 0, videos: 0 };
  const events = data?.events || [];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="wall-of-wins-page">
      <Helmet>
        <title>Wall of Wins — Real Creatara users earning real money | Creatara</title>
        <meta name="description" content="Live, anonymised activity feed of creators using Creatara to generate content, schedule posts, and earn from affiliates. Real signups, real clicks, real wins — updated continuously." />
        <link rel="canonical" href="https://creatara.ai/wins" />
        <meta property="og:title" content="Creatara — Wall of Wins" />
        <meta property="og:description" content="See real creators monetise their content in real time." />
      </Helmet>

      {/* Top nav (minimal — public page) */}
      <nav className="border-b border-white/5 sticky top-0 z-40 bg-background/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-500" /> Creatara
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/leaderboard" className="text-sm text-muted-foreground hover:text-foreground" data-testid="wins-nav-leaderboard">Leaderboard</Link>
            <Link to="/vs" className="text-sm text-muted-foreground hover:text-foreground hidden sm:inline">Compare</Link>
            <Link to="/auth">
              <Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="wins-nav-signup">Start free</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-6 pt-16 pb-10">
        <Badge className="bg-teal-500/15 text-teal-400 border-teal-500/30 mb-4" data-testid="live-badge">
          <span className="w-2 h-2 rounded-full bg-teal-400 mr-2 animate-pulse" />Live · updates every 30s
        </Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3 leading-tight">
          Real creators. Real wins. <span className="text-violet-500">Right now.</span>
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
          This is the live, anonymised activity feed of everyone using Creatara to write, schedule, and monetise their content. No fake counters, no vanity metrics — just what happened in the last 30 days.
        </p>
      </header>

      {/* Stat strip */}
      <section className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="wins-totals">
        <Stat label="Creators" value={totals.users} icon={Users} accent="text-teal-400" />
        <Stat label="Content generated" value={totals.content_pieces} icon={FileText} accent="text-cyan-400" />
        <Stat label="Affiliate clicks" value={totals.affiliate_clicks} icon={MousePointer2} accent="text-amber-400" />
        <Stat label="AI videos shipped" value={totals.videos} icon={Video} accent="text-purple-400" />
      </section>

      {/* Feed */}
      <section className="max-w-3xl mx-auto px-6 pb-20">
        <h2 className="text-xl font-bold mb-4">Recent activity</h2>
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
        ) : events.length === 0 ? (
          <p className="text-muted-foreground text-center py-20">
            The wall is warming up — be one of the first wins. <Link to="/auth" className="text-violet-500 underline">Sign up free</Link>.
          </p>
        ) : (
          <div className="space-y-2" data-testid="wins-feed">
            {events.map((e, i) => {
              const Icon = ICONS[e.kind] || Sparkles;
              const tone = KIND_TONE[e.kind] || "text-foreground bg-white/5 border-white/10";
              return (
                <div
                  key={`${e.ts}-${i}`}
                  className={`flex items-start gap-3 p-3 rounded-xl border ${tone} transition-all hover:scale-[1.005]`}
                  data-testid={`win-${i}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-black/30 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-semibold">{e.actor}</span>
                      {e.plan && e.plan !== "free" && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider opacity-70">{e.plan}</span>
                      )}
                      <span className="text-muted-foreground"> {e.verb}</span>
                    </p>
                    {e.detail && (
                      <p className="text-xs text-muted-foreground/70 truncate mt-0.5">"{e.detail}"</p>
                    )}
                  </div>
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap pt-1">{e.ago}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-violet-600/15 to-violet-700/5 border border-violet-500/30 text-center">
          <h3 className="text-xl font-bold mb-2">Your name could be on this wall tomorrow.</h3>
          <p className="text-sm text-muted-foreground mb-4">Free forever plan. {CLAIMS.freeAllowance.generations} generations / {CLAIMS.freeAllowance.period}. No card.</p>
          <Link to="/auth">
            <Button size="lg" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="wins-bottom-cta">
              Start earning with AI content <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
      <Icon className={`w-5 h-5 ${accent} mb-2`} />
      <p className="text-2xl font-bold">{(value || 0).toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
